import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";

import {
  getDashboardInsightsUnitId,
  setDashboardInsightsUnitId,
} from "../../../helpers/dashboardInsightsUnitPreference";
import { resolveCurrentTenantId } from "../../../helpers/agendaUnitContext";
import { formatMoneyFromCents } from "../../../helpers/money";
import {
  DashboardInsightSparklineWeek,
  DashboardInsightUnitSparkline,
  DashboardInsights,
  DashboardInsightsPeriodDays,
  getDashboardInsights,
} from "../../../services/dashboardInsightsService";
import { getUnits, Unit } from "../../../services/unitService";

const PERIOD_OPTIONS: DashboardInsightsPeriodDays[] = [1, 7, 30, 90];

const formatDeltaPercent = (deltaBps: number | null | undefined): string | null => {
  if (deltaBps === null || deltaBps === undefined) {
    return null;
  }

  const percent = deltaBps / 100;
  const sign = percent > 0 ? "+" : "";

  return `${sign}${percent.toFixed(percent % 1 === 0 ? 0 : 1)}%`;
};

const formatDayMonth = (iso: string | undefined, locale: string): string | null => {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
  }).format(date);
};

type SparklineProps = {
  weeks: DashboardInsightSparklineWeek[];
  title: string;
  formatMoney: (cents: number | null | undefined) => string;
};

const InsightsSparkline = ({ weeks, title, formatMoney }: SparklineProps) => {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<DashboardInsightSparklineWeek | null>(null);

  const maxCents = useMemo(
    () => Math.max(...weeks.map((week) => week.average_ticket_cents ?? 0), 1),
    [weeks]
  );

  if (weeks.length === 0) {
    return null;
  }

  const rangeLabel = selected
    ? (() => {
        const from = formatDayMonth(selected.from, i18n.language);
        const to = formatDayMonth(selected.to, i18n.language);
        if (from && to) {
          return `${from} – ${to}`;
        }
        return t("studio.dashboard.insights.sparklineWeek", {
          week: selected.week_index,
        });
      })()
    : null;

  return (
    <div className="mt-3">
      <p className="text-muted small mb-1">{title}</p>
      <p className="text-muted small mb-2">{t("studio.dashboard.insights.sparklineHint")}</p>
      <div className="d-flex align-items-end gap-2" style={{ height: 56 }}>
        {weeks.map((week) => {
          const cents = week.average_ticket_cents ?? 0;
          const height = Math.max(6, Math.round((cents / maxCents) * 48));
          const active = selected?.week_index === week.week_index;
          return (
            <button
              key={`spark-${week.week_index}`}
              type="button"
              className="btn btn-link p-0 text-decoration-none flex-fill"
              style={{ height: "100%" }}
              onClick={() =>
                setSelected((current) =>
                  current?.week_index === week.week_index ? null : week
                )
              }
              aria-label={t("studio.dashboard.insights.sparklineWeek", {
                week: week.week_index,
              })}
            >
              <div className="d-flex flex-column align-items-center justify-content-end h-100 gap-1">
                <div
                  className={`rounded-1 w-50 ${active ? "bg-warning" : "bg-primary"}`}
                  style={{ height, minHeight: 6 }}
                />
                <span className="small text-muted">S{week.week_index}</span>
              </div>
            </button>
          );
        })}
      </div>
      {selected ? (
        <div className="border rounded p-2 mt-2 bg-light">
          <div className="fw-medium small mb-1">{rangeLabel}</div>
          <div className="small text-muted mb-0">
            {t("studio.dashboard.insights.sparklineDetailTicket", {
              value: formatMoney(selected.average_ticket_cents),
            })}
          </div>
          <div className="small text-muted mb-0">
            {t("studio.dashboard.insights.sparklineDetailOrders", {
              count: selected.paid_orders_count,
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const UnitSparklineCompare = ({
  rows,
  formatMoney,
}: {
  rows: DashboardInsightUnitSparkline[];
  formatMoney: (cents: number | null | undefined) => string;
}) => {
  const { t } = useTranslation();

  if (rows.length < 2) {
    return null;
  }

  return (
    <Card className="mb-0 mt-3">
      <CardHeader className="py-3">
        <h6 className="card-title mb-1">
          {t("studio.dashboard.insights.sparklineByUnitTitle")}
        </h6>
        <p className="text-muted small mb-0">
          {t("studio.dashboard.insights.sparklineByUnitSubtitle")}
        </p>
      </CardHeader>
      <CardBody className="pt-0">
        <Row className="g-3">
          {rows.map((row) => (
            <Col md={6} key={row.unit_id}>
              <InsightsSparkline
                weeks={row.weeks}
                title={row.unit_name}
                formatMoney={formatMoney}
              />
            </Col>
          ))}
        </Row>
      </CardBody>
    </Card>
  );
};

const DashboardInsightsPanel = () => {
  const { t, i18n } = useTranslation();
  const ticketTooltipId = useId().replace(/:/g, "");
  const [loading, setLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [insights, setInsights] = useState<DashboardInsights | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitId, setUnitId] = useState<string>("");
  const [periodDays, setPeriodDays] = useState<DashboardInsightsPeriodDays>(30);
  const [unitPreferenceReady, setUnitPreferenceReady] = useState(false);

  const loadUnits = useCallback(async () => {
    setUnitsLoading(true);

    try {
      const response = await getUnits({ page: 1, per_page: 100 });
      const nextUnits = response.data ?? [];
      setUnits(nextUnits);

      const tenantId = resolveCurrentTenantId();
      if (tenantId) {
        const stored = getDashboardInsightsUnitId(tenantId);
        if (stored !== null && nextUnits.some((unit) => unit.id === stored)) {
          setUnitId(String(stored));
        }
      }
    } catch {
      setUnits([]);
    } finally {
      setUnitsLoading(false);
      setUnitPreferenceReady(true);
    }
  }, []);

  const loadInsights = useCallback(async () => {
    if (!unitPreferenceReady) {
      return;
    }

    setLoading(true);

    try {
      const parsedUnitId = unitId ? Number(unitId) : null;
      const response = await getDashboardInsights(
        Number.isFinite(parsedUnitId) && parsedUnitId !== null && parsedUnitId > 0
          ? parsedUnitId
          : null,
        periodDays
      );
      setInsights(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.dashboard.insights.toast.loadError"));
      setInsights(null);
    } finally {
      setLoading(false);
    }
  }, [periodDays, t, unitId, unitPreferenceReady]);

  useEffect(() => {
    void loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    void loadInsights();
  }, [loadInsights]);

  const handleUnitChange = (nextValue: string) => {
    setUnitId(nextValue);
    const tenantId = resolveCurrentTenantId();
    if (tenantId) {
      const parsed = nextValue ? Number(nextValue) : null;
      setDashboardInsightsUnitId(
        tenantId,
        Number.isFinite(parsed) && parsed !== null && parsed > 0 ? parsed : null
      );
    }
  };

  const formatMoney = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined) {
      return t("studio.dashboard.insights.emptyValue");
    }

    return formatMoneyFromCents(cents, i18n.language);
  };

  const showUnitFilter = units.length > 1;
  const deltaLabel = formatDeltaPercent(insights?.average_ticket_delta_bps);
  const deltaPositive =
    insights?.average_ticket_delta_bps !== null &&
    insights?.average_ticket_delta_bps !== undefined &&
    insights.average_ticket_delta_bps > 0;
  const deltaNegative =
    insights?.average_ticket_delta_bps !== null &&
    insights?.average_ticket_delta_bps !== undefined &&
    insights.average_ticket_delta_bps < 0;
  const showUnitCompare =
    !unitId && (insights?.ticket_sparkline_by_unit?.length ?? 0) > 1;

  const revenueCents = insights?.revenue_cents ?? 0;
  const averageTicket =
    insights?.average_ticket_cents ?? insights?.average_ticket_cents_30d ?? null;
  const appointmentsCount = insights?.appointments_count ?? 0;
  const attendanceBps = insights?.attendance_rate_bps ?? null;
  const paidCount = insights?.paid_orders_count ?? insights?.paid_orders_count_30d ?? 0;

  return (
    <div className="mb-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h5 className="mb-1">{t("studio.dashboard.insights.title")}</h5>
          <p className="text-muted mb-0 small">{t("studio.dashboard.insights.subtitle")}</p>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="btn-group" role="group" aria-label={t("studio.dashboard.insights.periodLabel")}>
            {PERIOD_OPTIONS.map((days) => {
              const active = periodDays === days;
              return (
                <Button
                  key={days}
                  type="button"
                  size="sm"
                  color={active ? "success" : "secondary"}
                  outline={!active}
                  className={active ? undefined : "bg-white text-body border-secondary-subtle"}
                  disabled={loading}
                  onClick={() => setPeriodDays(days)}
                >
                  {days === 1
                    ? t("studio.dashboard.insights.periodToday")
                    : t("studio.dashboard.insights.periodDays", { count: days })}
                </Button>
              );
            })}
          </div>
          {showUnitFilter ? (
            <div className="d-flex align-items-center gap-2" style={{ minWidth: "12rem" }}>
              <label className="text-muted small mb-0 text-nowrap" htmlFor="dashboard-insights-unit">
                {t("studio.dashboard.insights.unitFilter")}
              </label>
              <Input
                id="dashboard-insights-unit"
                type="select"
                bsSize="sm"
                value={unitId}
                disabled={unitsLoading || loading}
                onChange={(event) => handleUnitChange(event.target.value)}
              >
                <option value="">{t("studio.dashboard.insights.allUnits")}</option>
                {units.map((unit) => (
                  <option key={unit.id} value={String(unit.id)}>
                    {unit.name}
                  </option>
                ))}
              </Input>
            </div>
          ) : null}
        </div>
      </div>

      {loading || !unitPreferenceReady ? (
        <Card className="mb-0">
          <CardBody className="d-flex align-items-center gap-2 py-4">
            <Spinner size="sm" color="primary" />
            <span className="text-muted mb-0">{t("studio.dashboard.insights.loading")}</span>
          </CardBody>
        </Card>
      ) : !insights ? (
        <Alert color="warning" className="mb-0">
          {t("studio.dashboard.insights.loadFailed")}
        </Alert>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col xl={3} md={6}>
              <Card className="card-animate h-100 mb-0 border-0 shadow-sm">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted text-truncate mb-2">
                    {t("studio.dashboard.insights.revenue")}
                  </p>
                  <h4 className="fs-4 mb-1">{formatMoney(revenueCents)}</h4>
                  <p className="text-muted small mb-0">
                    {paidCount > 0
                      ? t("studio.dashboard.insights.revenueHint", { count: paidCount })
                      : t("studio.dashboard.insights.emptyPaidOrdersPeriod", {
                          days: periodDays,
                        })}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6}>
              <Card className="card-animate h-100 mb-0 border-0 shadow-sm">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted text-truncate mb-2">
                    {t("studio.dashboard.insights.averageTicket")}
                    <i
                      id={ticketTooltipId}
                      className="ri-information-line ms-1 align-middle"
                      role="img"
                      aria-label={t("studio.dashboard.insights.averageTicketTooltip")}
                    />
                    <UncontrolledTooltip placement="top" target={ticketTooltipId}>
                      {t("studio.dashboard.insights.averageTicketTooltip")}
                    </UncontrolledTooltip>
                  </p>
                  <h4 className="fs-4 mb-1">{formatMoney(averageTicket)}</h4>
                  {deltaLabel ? (
                    <p
                      className={`small mb-0 ${
                        deltaPositive
                          ? "text-success"
                          : deltaNegative
                            ? "text-danger"
                            : "text-muted"
                      }`}
                    >
                      {t("studio.dashboard.insights.averageTicketDelta", {
                        delta: deltaLabel,
                      })}
                    </p>
                  ) : (
                    <p className="text-muted small mb-0">
                      {t("studio.dashboard.insights.ticketNeedsPaidOrders")}
                    </p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6}>
              <Card className="card-animate h-100 mb-0 border-0 shadow-sm">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted text-truncate mb-2">
                    {t("studio.dashboard.insights.appointments")}
                  </p>
                  <h4 className="fs-4 mb-1">{appointmentsCount}</h4>
                  <p className="text-muted small mb-0">
                    {t("studio.dashboard.insights.appointmentsHint")}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6}>
              <Card className="card-animate h-100 mb-0 border-0 shadow-sm">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted text-truncate mb-2">
                    {t("studio.dashboard.insights.attendance")}
                  </p>
                  <h4 className="fs-4 mb-1">
                    {attendanceBps === null
                      ? t("studio.dashboard.insights.emptyValue")
                      : `${(attendanceBps / 100).toFixed(attendanceBps % 100 === 0 ? 0 : 1)}%`}
                  </h4>
                  <p className="text-muted small mb-0">
                    {t("studio.dashboard.insights.attendanceHint")}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col xl={4} md={6}>
              <Card className="card-animate h-100 mb-0">
                <CardBody>
                  <p className="text-uppercase fw-medium text-muted text-truncate mb-2">
                    {t("studio.dashboard.insights.creditsExpiring")}
                  </p>
                  <h4 className="fs-4 mb-1">{insights.credits_expiring_30d}</h4>
                  <Link
                    to="/clients?credits_expiring_within_days=30"
                    className="text-decoration-underline"
                  >
                    {t("studio.dashboard.insights.creditsLink")}
                  </Link>
                  {(insights.ticket_sparkline_4w?.length ?? 0) > 0 ? (
                    <InsightsSparkline
                      weeks={insights.ticket_sparkline_4w ?? []}
                      title={t("studio.dashboard.insights.sparklineTitle")}
                      formatMoney={formatMoney}
                    />
                  ) : null}
                </CardBody>
              </Card>
            </Col>

            <Col xl={4} md={6}>
              <Card className="h-100 mb-0">
                <CardHeader className="py-3">
                  <h6 className="card-title mb-0">
                    {t("studio.dashboard.insights.topServices")}
                  </h6>
                </CardHeader>
                <CardBody className="pt-0">
                  {insights.top_services.length === 0 ? (
                    <div>
                      <p className="text-muted mb-2 small">
                        {t("studio.dashboard.insights.emptyServices")}
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        <Link to="/orders" className="btn btn-soft-primary btn-sm">
                          {t("studio.dashboard.insights.emptyServicesOrdersCta")}
                        </Link>
                        <Link to="/services" className="btn btn-soft-secondary btn-sm">
                          {t("studio.dashboard.insights.emptyServicesCatalogCta")}
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <ul className="list-unstyled mb-0">
                      {insights.top_services.map((row, index) => (
                        <li
                          key={`${row.service_id ?? "svc"}-${index}`}
                          className="d-flex justify-content-between align-items-center py-1"
                        >
                          <span className="text-truncate me-2">{row.name}</span>
                          <Badge color="soft-primary" pill>
                            {row.quantity}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col xl={4} md={6}>
              <Card className="h-100 mb-0">
                <CardHeader className="py-3">
                  <h6 className="card-title mb-0">
                    {t("studio.dashboard.insights.topClients")}
                  </h6>
                </CardHeader>
                <CardBody className="pt-0">
                  {insights.top_clients.length === 0 ? (
                    <p className="text-muted mb-0 small">
                      {t("studio.dashboard.insights.emptyClients")}
                    </p>
                  ) : (
                    <ul className="list-unstyled mb-0">
                      {insights.top_clients.map((row) => (
                        <li
                          key={row.client_id}
                          className="d-flex justify-content-between align-items-center py-1"
                        >
                          <Link
                            to={`/clients/${row.client_id}`}
                            className="text-truncate me-2 text-decoration-none"
                          >
                            {row.name}
                          </Link>
                          <span className="small text-nowrap">
                            {formatMoney(row.total_amount_cents)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {showUnitCompare ? (
            <UnitSparklineCompare
              rows={insights.ticket_sparkline_by_unit ?? []}
              formatMoney={formatMoney}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default DashboardInsightsPanel;
